import { useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import ReportWorkforceHead from "./ReportSheets/ReportSheetsHead/ReportWorkforceHead";
import { REPORTS } from "../../utils/ReportConfig";
import { filtrationcontext } from "../../context/Filtercontext";
import WorkforceReportWidgets from "./ReportPage/ReportComponents/WorkforceReportWidgets";
import { toast } from "react-toastify";
import trans from "../../utils";
import LostOpportunitiesReport from "./ReportTables/inventory/LostOpportunitiesReport";

// Reports whose endpoint wraps its payload in {status, message, data: {...}}.
// getSummary() below already unwraps one level for them (data?.data); the table
// must match or it reads rows/pagination off the envelope and renders empty
// even though the API returned data.
const ENVELOPE_REPORTS = new Set([
	"productExpiration",
	"requests",
	"dailyAttendance",
	"attendance",
	"productivity",
]);

// ─── Guard wrapper ──────────────────────────────────────────────────────────
// Resolves shortPath and validates reportConfig BEFORE any hooks that depend on
// it are called. This satisfies React's rules of hooks.
const WorkforceReport = () => {
	const { pathname } = useLocation();
	const shortPath = pathname.split("/").pop();
	const reportConfig = REPORTS[shortPath];
	if (shortPath === "lostOpportunities" && process.env.REACT_APP_LOST_OPPORTUNITIES_ENABLED === "true") return <LostOpportunitiesReport />;

	if (!reportConfig) {
		return (
			<div className="p-4">
				<h5>{trans("Report not found")}</h5>
			</div>
		);
	}

	return (
		<WorkforceReportContent reportConfig={reportConfig} shortPath={shortPath} />
	);
};

// ─── Inner component ────────────────────────────────────────────────────────
// All hooks are safe here because reportConfig is guaranteed to be defined.
const WorkforceReportContent = ({ reportConfig, shortPath }) => {
	const { limit, Sort, searchQuery } = useContext(filtrationcontext);

	const [isShown, setIsShown] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [filters, setFilters] = useState({});

	const ReportTable = reportConfig.component;
	const useReportApi = reportConfig.apiHook;

	const [triggerReport, { data, isFetching }] = useReportApi();
	const isFirstRender = useRef(true);

	const summary = reportConfig.getSummary(
		shortPath === "deviceAssignment"
			? data?.statistics
			: shortPath === "shelfAvailability"
				? data?.summary
				: shortPath === "productExpiration"
					? data?.data?.summary
					: data?.data,
	);
	const WidgetsComponent =
		reportConfig.widgetsComponent || WorkforceReportWidgets;

	useEffect(() => {
		setCurrentPage(1);
	}, [searchQuery, filters]);

	useEffect(() => {
		if (Object.keys(filters).length) {
			if (!filters?.startDate || !filters?.endDate) {
				if (isShown) {
					toast.error(trans("Please Select Date Range"), {
						autoClose: 800,
						rtl: localStorage.getItem("lang") === "ar" ? true : false,
					});
					setIsShown(false);
				}
			} else {
				handleFetchReport();
			}
			isFirstRender.current = false;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filters, Sort, isShown]);

	useEffect(() => {
		if (!isFirstRender.current) {
			if (filters?.startDate && filters?.endDate) {
				handleFetchReport();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPage, limit]);

	const normalizeFilter = (val) => {
		if (val === null || val === undefined) return undefined;
		if (Array.isArray(val) && val.length === 0) return undefined;
		if (typeof val === "string" && val.trim() === "") return undefined;
		return val;
	};

	function formatDate(dateString) {
		return decodeURIComponent(dateString).split("T")[0];
	}

	const handleFetchReport = () => {
		if (shortPath === "deviceAssignment") {
			triggerReport({
				page: currentPage,
				limit: limit,
				date_from: normalizeFilter(filters.startDate),
				date_to: normalizeFilter(filters.endDate),
				search: searchQuery,
				status: normalizeFilter(filters.status),
				userId: normalizeFilter(filters.assigneeIds),
				sortBy: Sort.by,
				sortOrder: Sort.order,
			});
		} else if (shortPath === "shelfAvailability") {
			triggerReport({
				page: currentPage,
				limit: limit,
				fromDate: normalizeFilter(filters.startDate),
				toDate: normalizeFilter(filters.endDate),
				formId: process.env.REACT_APP_SHELF_FORM_ID,
				storeId: normalizeFilter(filters.storeId),
				sku: normalizeFilter(filters.sku),
				repId: normalizeFilter(filters.repId),
				states: normalizeFilter(filters.states),
				storeTypes: filters.tableStoreTypes?.join(",") || undefined,
				categoryIds: filters.tableCategories?.join(",") || undefined,
				cities: filters.tableCities?.join(",") || undefined,
				sortBy: Sort.by,
				sortOrder: Sort.order,
				search: searchQuery,
			});
		} else if (shortPath === "shelfMovement") {
			triggerReport({
				page: currentPage,
				limit: limit,
				fromDate: formatDate(filters.startDate),
				toDate: formatDate(filters.endDate),
				skuIds: filters.skuIds?.join(",") || undefined,
				poiIds: filters.poiIds?.join(",") || undefined,
				state: filters.state || undefined,
				cities: filters.tableCities?.join(",") || undefined,
				sortBy: Sort.by,
				sortOrder: Sort.order,
			});
		} else if (shortPath === "productExpiration") {
			// Table column filters take precedence over offcanvas status filter
			const statuses = filters.tableStatuses?.length
				? filters.tableStatuses.join(",")
				: normalizeFilter(filters.expiryStatus);
			triggerReport({
				page: currentPage,
				limit: limit,
				from: normalizeFilter(filters.startDate),
				to: normalizeFilter(filters.endDate),
				criticalDays: normalizeFilter(filters.criticalDays) ?? 7,
				warningDays: normalizeFilter(filters.warningDays) ?? 30,
				noticeDays: normalizeFilter(filters.noticeDays),
				statuses,
				skuIds: filters.tableSkuIds?.join(",") || undefined,
				poiIds: filters.tablePoiIds?.join(",") || undefined,
				recordedByIds: filters.tableRecordedByIds?.join(",") || undefined,
				categoryIds: filters.tableCategories?.join(",") || undefined,
				poiTypes: filters.tablePoiTypes?.join(",") || undefined,
				trackingContexts: filters.tableItemPlaces?.join(",") || undefined,
				cities: filters.tableCities?.join(",") || undefined,
				sortBy: Sort.by,
				sortOrder: Sort.order,
			});
		} else {
			triggerReport({
				page: currentPage,
				limit: limit,
				fromDate: normalizeFilter(filters.startDate),
				toDate: normalizeFilter(filters.endDate),
				serviceType: normalizeFilter(filters.serviceType),
				status: normalizeFilter(filters.status),
				country: normalizeFilter(filters.country),
				state: normalizeFilter(filters.state),
				city: normalizeFilter(filters.city),
				poiIds: normalizeFilter(filters.poiId),
				assigneeIds: normalizeFilter(filters.assigneeIds),
				requesterIds: normalizeFilter(filters.requesterIds),
				sortBy: Sort.by,
				sortOrder: Sort.order,
				efficiencyThreshold: normalizeFilter(filters.efficiencyThreshold),
				search: searchQuery,
				technicianIds: normalizeFilter(filters.technicianIds),
			});
		}
	};

	return (
		<>
			<ReportWorkforceHead
				startDate={filters?.startDate || ""}
				endDate={filters?.endDate || ""}
				isShown={isShown}
				setIsShown={setIsShown}
				filters={filters}
				setFilters={setFilters}
				onSubmit={handleFetchReport}
				reportType={shortPath}
			/>
			<div className="report-view">
				<div className="report-sheets">
					{(data?.statistics ||
						(data?.data && shortPath !== "shelfMovement") ||
						(shortPath === "shelfAvailability" && data?.summary) ||
						(shortPath === "productExpiration" && data?.data?.summary)) && (
						<WidgetsComponent
							summary={summary}
							reportType={shortPath}
							filters={filters}
							setFilters={setFilters}
						/>
					)}
					<ReportTable
						startDate={filters.startDate}
						endDate={filters.endDate}
						currentPage={currentPage}
						setCurrentPage={setCurrentPage}
						data={
							ENVELOPE_REPORTS.has(shortPath) ? data?.data || null : data || null
						}
						isLoading={isFetching}
						selectedids={filters.technicianIds}
						filters={filters}
						setFilters={setFilters}
					/>
				</div>
			</div>
		</>
	);
};

export default WorkforceReport;
